import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  LayoutTemplate, Plus, Edit, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  Upload, Link as LinkIcon, Image as ImageIcon, Film, Images, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/admin/landing-assets`;

const SECTIONS = [
  { value: 'hero_banner', label: 'Hero Banner' },
  { value: 'brands', label: 'Brands using LoopAi' },
  { value: 'video_creator', label: 'Video Creator' },
  { value: 'image_creator', label: 'Image Creator' },
  { value: 'movie_maker', label: 'Movie Maker' },
  { value: 'loopai_in_action', label: 'LoopAi in Action' },
];

const MEDIA_TYPES = [
  { value: 'image', label: 'Image (upload or URL)' },
  { value: 'video', label: 'Video (upload or URL)' },
  { value: 'carousel', label: 'Carousel (multiple items)' },
  { value: 'url', label: 'External URL (YouTube / Vimeo)' },
];

const emptyForm = {
  media_type: 'image', title: '', description: '',
  external_url: '', thumbnail_url: '', sort_order: '', is_active: true,
  items: [],
};

const mediaIcon = (t) => ({
  image: ImageIcon, video: Film, carousel: Images, url: LinkIcon,
}[t] || ImageIcon);

export default function LandingPageAssets() {
  const [activeSection, setActiveSection] = useState('hero_banner');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [mediaFile, setMediaFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}?section=${activeSection}&include_inactive=true`, { headers });
      setAssets(res.data.assets || []);
    } catch (err) {
      toast.error('Failed to fetch landing assets');
    } finally { setLoading(false); }
  }, [activeSection]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setMediaFile(null); setThumbFile(null); setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      media_type: item.media_type || 'image',
      title: item.title || '', description: item.description || '',
      external_url: item.external_url || '',
      thumbnail_url: item.thumbnail_url || '',
      sort_order: item.sort_order ?? '', is_active: item.is_active !== false,
      items: (item.items || []).map(it => ({ type: it.type || 'image', caption: it.caption || '', url: it.url || '', file: null })),
    });
    setMediaFile(null); setThumbFile(null); setShowDialog(true);
  };

  // ---- carousel item helpers ----
  const addCarouselItem = () => setForm(f => ({ ...f, items: [...f.items, { type: 'image', caption: '', url: '', file: null }] }));
  const updateCarouselItem = (i, patch) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const removeCarouselItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      if (!editing) fd.append('section', activeSection);
      fd.append('media_type', form.media_type);
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('is_active', form.is_active);
      if (form.sort_order !== '') fd.append('sort_order', form.sort_order);
      if (form.thumbnail_url) fd.append('thumbnail_url', form.thumbnail_url);
      if (thumbFile) fd.append('thumbnail_file', thumbFile);

      if (form.media_type === 'image' || form.media_type === 'video') {
        if (mediaFile) fd.append('media_file', mediaFile);
        else if (form.external_url) fd.append('external_url', form.external_url);
      } else if (form.media_type === 'url') {
        fd.append('external_url', form.external_url);
      } else if (form.media_type === 'carousel') {
        const meta = form.items.map((it, i) => {
          const m = { type: it.type, caption: it.caption };
          if (it.file) { m.file_key = `item_file_${i}`; fd.append(`item_file_${i}`, it.file); }
          else { m.url = it.url; }
          return m;
        });
        fd.append('items', JSON.stringify(meta));
      }

      if (editing) {
        await axios.put(`${API}/${editing.id}`, fd, { headers });
        toast.success('Asset updated');
      } else {
        await axios.post(API, fd, { headers });
        toast.success('Asset created');
      }
      setShowDialog(false); fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save asset');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers });
      toast.success('Asset deleted'); fetchAssets();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const toggleActive = async (item) => {
    try {
      const fd = new FormData();
      fd.append('is_active', !item.is_active);
      await axios.put(`${API}/${item.id}`, fd, { headers });
      fetchAssets();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const moveItem = async (index, direction) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= assets.length) return;
    const order = assets.map((item, i) => {
      if (i === index) return { id: item.id, sort_order: assets[swapIdx].sort_order };
      if (i === swapIdx) return { id: item.id, sort_order: assets[index].sort_order };
      return { id: item.id, sort_order: item.sort_order };
    });
    try {
      await axios.put(`${API}/reorder`, { order }, { headers: { ...headers, 'Content-Type': 'application/json' } });
      fetchAssets();
    } catch (err) { toast.error('Failed to reorder'); }
  };

  const previewUrl = (item) => item.file_path || item.external_url;

  const renderPreview = (item) => {
    const url = previewUrl(item);
    if (item.media_type === 'carousel') {
      const items = item.items || [];
      return (
        <div className="flex gap-1 overflow-hidden">
          {items.slice(0, 4).map((it, i) => (
            it.type === 'video'
              ? <div key={i} className="w-14 h-14 rounded bg-slate-800 flex items-center justify-center"><Film className="w-5 h-5 text-white" /></div>
              : <img key={i} src={it.url} alt="" className="w-14 h-14 rounded object-cover bg-slate-100" />
          ))}
          {items.length > 4 && <div className="w-14 h-14 rounded bg-slate-100 flex items-center justify-center text-xs text-slate-500">+{items.length - 4}</div>}
          {items.length === 0 && <span className="text-xs text-slate-400">No items</span>}
        </div>
      );
    }
    if (item.media_type === 'image' && url) return <img src={url} alt="" className="w-20 h-14 rounded object-cover bg-slate-100" />;
    if (item.media_type === 'video' && item.file_path) return <video src={item.file_path} className="w-20 h-14 rounded object-cover bg-slate-900" muted />;
    if ((item.media_type === 'video' || item.media_type === 'url')) {
      return (
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline max-w-[180px] truncate">
          <LinkIcon className="w-3 h-3 shrink-0" />{url || '—'}
        </a>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  const isCarousel = form.media_type === 'carousel';
  const isUrl = form.media_type === 'url';
  const isUpload = form.media_type === 'image' || form.media_type === 'video';

  return (
    <div data-testid="landing-assets-page">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 data-testid="landing-assets-title" className="text-4xl font-bold text-slate-800 mb-2">Landing Page Assets</h1>
          <p className="text-slate-600">Manage the visuals and videos shown on each section of the LoopAI landing page</p>
        </div>
        <Button data-testid="add-landing-asset-btn" onClick={openCreate} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" /> Add Asset
        </Button>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map(s => (
          <button
            key={s.value}
            data-testid={`section-tab-${s.value}`}
            onClick={() => setActiveSection(s.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeSection === s.value ? 'bg-purple-600 text-white shadow' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-500">Loading assets...</div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg shadow-md">
          <LayoutTemplate className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500 mb-4">No assets in “{SECTIONS.find(s => s.value === activeSection)?.label}” yet</p>
          <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700"><Plus className="w-4 h-4 mr-2" /> Add First Asset</Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full" data-testid="landing-assets-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-16">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-44">Preview</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-28">Type</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-20">Active</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map((item, idx) => {
                const MIcon = mediaIcon(item.media_type);
                return (
                  <tr key={item.id} className={`hover:bg-slate-50 transition-colors ${!item.is_active ? 'opacity-50' : ''}`} data-testid={`landing-asset-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center gap-0.5">
                        <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid={`landing-asset-up-${item.id}`}><ChevronUp className="w-4 h-4" /></button>
                        <span className="text-xs text-slate-500">{item.sort_order}</span>
                        <button onClick={() => moveItem(idx, 1)} disabled={idx === assets.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid={`landing-asset-down-${item.id}`}><ChevronDown className="w-4 h-4" /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3">{renderPreview(item)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{item.title || <span className="text-slate-400 italic">Untitled</span>}</div>
                      {item.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        <MIcon className="w-3 h-3" />{item.media_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggleActive(item)} data-testid={`landing-asset-toggle-${item.id}`} className="inline-flex">
                        {item.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(item)} data-testid={`landing-asset-edit-${item.id}`} className="p-1.5 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item.id)} data-testid={`landing-asset-delete-${item.id}`} className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[660px] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Asset' : `Add Asset — ${SECTIONS.find(s => s.value === activeSection)?.label}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Media Type *</Label>
                  <Select value={form.media_type} onValueChange={v => setForm({ ...form, media_type: v })}>
                    <SelectTrigger data-testid="landing-form-media-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{MEDIA_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Input data-testid="landing-form-sort-order" type="number" placeholder="Auto" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Title</Label>
                <Input data-testid="landing-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Optional label / heading" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="landing-form-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>

              {/* Single image / video */}
              {isUpload && (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <Label className="text-sm font-semibold">{form.media_type === 'image' ? 'Image Source' : 'Video Source'}</Label>
                  <div>
                    <Label className="text-xs text-slate-500">{form.media_type === 'image' ? 'Image URL' : 'Video URL (YouTube, Vimeo, CDN)'}</Label>
                    <Input data-testid="landing-form-external-url" placeholder="https://..." value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} />
                  </div>
                  <div className="text-center text-xs text-slate-400">or upload a file</div>
                  <div>
                    <Label className="text-xs text-slate-500">Upload {form.media_type === 'image' ? 'Image' : 'Video'}</Label>
                    <Input data-testid="landing-form-media-file" type="file" accept={form.media_type === 'image' ? 'image/*' : 'video/*'} onChange={e => setMediaFile(e.target.files[0])} />
                  </div>
                  {editing?.file_path && !mediaFile && <p className="text-xs text-emerald-600 flex items-center gap-1"><Upload className="w-3 h-3" />Current file kept unless replaced</p>}
                </div>
              )}

              {/* External URL only */}
              {isUrl && (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <Label className="text-sm font-semibold">External Video URL</Label>
                  <Input data-testid="landing-form-external-url" placeholder="https://youtube.com/... or https://vimeo.com/..." value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} required />
                </div>
              )}

              {/* Carousel */}
              {isCarousel && (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Carousel Items ({form.items.length})</Label>
                    <Button type="button" size="sm" variant="outline" data-testid="landing-form-add-item" onClick={addCarouselItem}><Plus className="w-3 h-3 mr-1" />Add Item</Button>
                  </div>
                  {form.items.length === 0 && <p className="text-xs text-slate-400">No items yet. Add images, videos or links.</p>}
                  {form.items.map((it, i) => (
                    <div key={i} className="border rounded-md p-3 bg-white space-y-2" data-testid={`landing-form-item-${i}`}>
                      <div className="flex items-center gap-2">
                        <Select value={it.type} onValueChange={v => updateCarouselItem(i, { type: v, file: null, url: '' })}>
                          <SelectTrigger data-testid={`landing-form-item-type-${i}`} className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                            <SelectItem value="url">URL</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input className="h-8 flex-1" placeholder="Caption (optional)" value={it.caption} onChange={e => updateCarouselItem(i, { caption: e.target.value })} />
                        <button type="button" onClick={() => removeCarouselItem(i)} data-testid={`landing-form-item-remove-${i}`} className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100"><X className="w-4 h-4" /></button>
                      </div>
                      {it.type === 'url' ? (
                        <Input className="h-8" placeholder="https://..." value={it.url} onChange={e => updateCarouselItem(i, { url: e.target.value })} />
                      ) : (
                        <div className="space-y-1">
                          <Input className="h-8" type="file" accept={it.type === 'image' ? 'image/*' : 'video/*'} data-testid={`landing-form-item-file-${i}`} onChange={e => updateCarouselItem(i, { file: e.target.files[0] })} />
                          {it.url && !it.file && <p className="text-[11px] text-slate-400 truncate">Current: {it.url}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Thumbnail (optional) */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Thumbnail (optional)</Label>
                <Input data-testid="landing-form-thumb-url" placeholder="Thumbnail URL" value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} />
                <div className="text-center text-xs text-slate-400">or upload</div>
                <Input data-testid="landing-form-thumb-file" type="file" accept="image/*" onChange={e => setThumbFile(e.target.files[0])} />
              </div>

              <div className="flex items-center gap-3">
                <Switch data-testid="landing-form-active" checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label>Active (visible on landing page)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="landing-form-submit" type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
